import {
    dehydrate,
    type DehydratedState,
    hydrate,
    type Query,
    QueryClient,
    VueQueryPlugin,
    type VueQueryPluginOptions
} from "@tanstack/vue-query";
import {get, set} from 'idb-keyval';
import {MS_SECOND} from "~/constants/time";

export const queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: 1000 } },
})

// An empty question list is never real data; a snapshot holding one would hide every question for an hour
const QUESTION_LIST_KEYS = ['questionGetAllQuestionDefinitions', 'questionGetAllQuestionTemplates']
const isEmptyQuestionList = (query: Query) =>
    QUESTION_LIST_KEYS.includes(String(query.queryKey[0])) && Array.isArray(query.state.data) && query.state.data.length === 0

const snapshot = () => dehydrate(queryClient, {
    shouldDehydrateQuery: (query) => query.state.status === 'success' && !isEmptyQuestionList(query),
})

export default defineNuxtPlugin((nuxt) => {
    const options: VueQueryPluginOptions = { queryClient }

    nuxt.vueApp.use(VueQueryPlugin, options)

    // Hydrate from IndexedDB
    nuxt.hooks.hook("app:created", async () => {
        try {
            const savedState = await get<DehydratedState>('vue-query')
            if (savedState) {
                hydrate(queryClient, savedState)
                queryClient.getQueryCache().findAll().filter(isEmptyQuestionList)
                    .forEach((query) => queryClient.invalidateQueries({queryKey: query.queryKey}))
            }
        } catch (error) {
            console.error("Failed to hydrate Vue Query cache from IndexedDB", error)
        }

        // Auto-persist by interval
        const interval = setInterval(async () => {
            try {
                await set('vue-query', snapshot())
                console.log('Query state has been persisted')
            } catch (error) {
                console.error("Failed to auto-persist Vue Query cache to IndexedDB", error)
            }
        }, 30 * MS_SECOND)

        window.addEventListener("beforeunload", () => {
            clearInterval(interval)
        })
    })

    // Persist to IndexedDB before window unload
    window.addEventListener("beforeunload", async () => {
        try {
            await set('vue-query', snapshot())
        } catch (error) {
            console.error("Failed to persist Vue Query cache to IndexedDB", error)
        }
    })
})
